/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import * as _ from "lodash";
import { BLUE_COLOR, MAX_Z_INDEX, PERCENTAGE_HUNDRED } from "../../Constants";
import { IScaleObjects } from "../../store/ApiContracts";
import { getRotationFlipMatrix, getShapeCenter, mapPointToMatrix, manipulateDimensions, xor } from "../../store/reducers/util";
import { SelectionContainer, ShapeContainer } from "../containers/ShapeContainer";
import { IApiSet } from "../injectors/ApiContext";
import { IGeoBoundaryMeta } from "../interfaces/model/dataStructures/GeoBoundaryMeta";
import { CanvasMode, FillTypes, GeometryTypes, PageObjectScalePointType, ShapeType } from "../interfaces/model/enumerations/index";
import { IPresetGeometry } from "../interfaces/model/geometry/PresetGeometry";
import { IBrowserType } from "../interfaces/model/index";
import { IShape, IGroupShape } from "../interfaces/model/PageObjects/index";
import { IPictureFillProperties } from "../interfaces/model/properties/index";
import { IDragResults, IPosition } from "../meta/Drag/index";
import { getGeoBoundaryMeta } from "../util";
import { EditCurvePoints } from "./EditCurvePoints";
import { ISelectionBoxRenderProps } from "./SelectionBox";

/**
 * Picture edit crop box render properties
 */
export interface IPictureCustomEditCropBoxRenderProps {

    /**
     * Api set
     */
    api: IApiSet;

    /**
     * Browser type on which the app is running
     */
    browserType: IBrowserType;

    /**
     * position, height, and width of the crop box
     */
    dimensions: any;

    /** Group object if base object is inside a group */
    groupObject?: IGroupShape;

    /**
     * group selection index if applicable
     */
    groupSelectionIndices?: number[];

    /**
     * Selected curve point
     */
    selectedCurvePoint?: number;

    /** Page object index */
    poIndex: number;

    /** Page object of type shape having image as fill and isPicture as true */
    pageObject: IShape;

    /** Shape render-able geo boundary meta */
    geoBoundaryMeta?: IGeoBoundaryMeta;

    /**
     * Scale object callback
     */
    scaleObjects: IScaleObjects;

    /** Set shape adjust values */
    setShapeAdjustValues: ISelectionBoxRenderProps["setShapeAdjustValues"];

    /**
     * Zoom factor value
     */
    zoomFactor: number;

    /**
     * Edit object callback.
     */
    editPageObject?(): void;

    /**
     * Translate object callback
     * @param delta Drag event delta
     */
    translateObjects?(delta: IDragResults, transient?: boolean): void;

    /**
     * Set all aspect ratios callback
     */
    setAllAspectRatios(transient: boolean): void;
}

/**
 * Picture edit crop box
 */
export class PictureCustomEditCropBox extends WidgetBase<IPictureCustomEditCropBoxRenderProps> {
	/**
     * Override WidgetBase#render to produce a virtual DOM tree.
     * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
     */
	protected render() {
		const { api, browserType, dimensions, selectedCurvePoint, groupObject } = this.properties;
		const { setCurvePoints } = api.pageApi;
		const { setSelectedCurvePoint } = api.canvasApi;
		let { pageObject } = this.properties;
		const groupFlip = groupObject ? groupObject.spPr.xfrm.flipH : false;
		// Page object transforms
		let pageObjectXfrm = pageObject.spPr.xfrm;
		// Ghost image object
		const ghostImage = _.cloneDeep(pageObject);
		// ghost image transforms
		const ghostXfrm = ghostImage.spPr.xfrm;
		const originalPageObject = pageObject;

		if (groupObject) {
			const posObj = manipulateDimensions.calculateSubgroupAbsoluteOffsets(pageObject.spPr, groupObject.spPr);

			ghostXfrm.off.x = posObj.offX;
			ghostXfrm.off.y = posObj.offY;
			ghostXfrm.rot = posObj.rot;

			pageObject = _.cloneDeep(pageObject);
			pageObjectXfrm = pageObject.spPr.xfrm;
			pageObjectXfrm.off.x = posObj.offX;
			pageObjectXfrm.off.y = posObj.offY;
			pageObjectXfrm.rot = posObj.rot;
			pageObjectXfrm.flipH = xor(pageObjectXfrm.flipH, groupObject.spPr.xfrm.flipH);
		}
		// Ghost fill source rectangle
		const ghostSrcRect = (ghostImage.spPr.fill as IPictureFillProperties).srcRect;

		// Provide unique shape id to ghost image, too so that it wont interfere with actual shape object.
		ghostImage.shapeId = ghostImage.shapeId + "_ghostImage";

		// Let the shape be rectangle for ghost image
		ghostImage.spPr.geometry = { presetShape : ShapeType.rect, type: GeometryTypes.PRESET } as IPresetGeometry;

		// Set flip and rotation for ghost image as that of srcRect
		ghostXfrm.flipH = xor(ghostSrcRect.flipH, groupFlip);
		ghostXfrm.rot = ghostSrcRect.rot + (groupObject ? groupObject.spPr.xfrm.rot : 0);

		// Set ghost shape outline to none
		ghostImage.spPr.ln = undefined;

		// Map the ghost image dimensions to local geometry boundary meta. Entries in this local meta would be
		// adjusted as per geometry boundary meta passed as properties. This will help to develop algorithm based on
		// geometry boundary meta only.
		const geoBoundaryMeta: IGeoBoundaryMeta = getGeoBoundaryMeta(ghostXfrm);

		// Adjust local geometry boundary meta per that of properties
		const geoBoundaryMetaProps = this.properties.geoBoundaryMeta;
		if (geoBoundaryMetaProps) {
			const boundaryOffDelta = { x: geoBoundaryMetaProps.xMin, y: geoBoundaryMetaProps.yMin };
			const boundaryExtDelta = {
				x: geoBoundaryMetaProps.xMax - pageObjectXfrm.ext.cx,
				y: geoBoundaryMetaProps.yMax - pageObjectXfrm.ext.cy,
			};

			ghostSrcRect.rot += (groupObject ? groupObject.spPr.xfrm.rot : 0);
			ghostSrcRect.flipH = xor(ghostSrcRect.flipH, groupFlip);

			// Effective flip for mapping boundary offset and extents delta
			const effectiveFlip =
                pageObjectXfrm.flipH ? !ghostSrcRect.flipH : ghostSrcRect.flipH;
			// Effective rotation for mapping boundary offset and extents delta
			const effectiveRot = pageObjectXfrm.rot - ghostSrcRect.rot;

			const mappedBoundaryOffDelta =
                mapPointToMatrix(getRotationFlipMatrix(effectiveRot, effectiveFlip), boundaryOffDelta);
			const mappedBoundaryExtDelta =
                mapPointToMatrix(getRotationFlipMatrix(effectiveRot, effectiveFlip), boundaryExtDelta);

			geoBoundaryMeta.width = Math.max(geoBoundaryMeta.width, geoBoundaryMetaProps.width);
			geoBoundaryMeta.height = Math.max(geoBoundaryMeta.height, geoBoundaryMetaProps.height);

			geoBoundaryMeta.xMin = geoBoundaryMeta.xMin + ((boundaryOffDelta.x + mappedBoundaryOffDelta.x) / 2);
			geoBoundaryMeta.yMin = geoBoundaryMeta.yMin + ((boundaryOffDelta.y + mappedBoundaryOffDelta.y) / 2);

			// Extents can never be in negative. When mapped, if boundary delta extents are negative then adjust those
			// in final boundary meta xMin and yMin
			if (mappedBoundaryExtDelta.x < 0) {
				geoBoundaryMeta.xMin = geoBoundaryMeta.xMin + mappedBoundaryExtDelta.x;
			}
			if (mappedBoundaryExtDelta.y < 0) {
				geoBoundaryMeta.yMin = geoBoundaryMeta.yMin + mappedBoundaryExtDelta.y;
			}
		}

		// Compute margins
		const topMargin = ghostSrcRect.t * geoBoundaryMeta.height / PERCENTAGE_HUNDRED;
		const leftMargin = ghostSrcRect.l * geoBoundaryMeta.width / PERCENTAGE_HUNDRED;
		const rightMargin = ghostSrcRect.r * geoBoundaryMeta.width / PERCENTAGE_HUNDRED;
		const bottomMargin = ghostSrcRect.b * geoBoundaryMeta.height / PERCENTAGE_HUNDRED;

		// Adjust ghost shape position and dimensions considering image margins
		ghostXfrm.off.x = geoBoundaryMeta.xMin + leftMargin;
		ghostXfrm.off.y = geoBoundaryMeta.yMin + topMargin;
		ghostXfrm.ext.cx = geoBoundaryMeta.width - (rightMargin + leftMargin);
		ghostXfrm.ext.cy = geoBoundaryMeta.height - (bottomMargin + topMargin);

		(ghostImage.spPr.fill as IPictureFillProperties).srcRect = {
			b: 0, l: 0, r: 0, t: 0, flipH: ghostSrcRect.flipH, rot: ghostSrcRect.rot,
			refExt: { cx: ghostXfrm.ext.cx, cy: ghostXfrm.ext.cy },
		};

		// Prepare transform origin for ghost shape and its selection
		const ghostTransformOrigin = getShapeCenter(pageObject);
		ghostTransformOrigin.x = ghostTransformOrigin.x - leftMargin - geoBoundaryMeta.xMin;
		ghostTransformOrigin.y = ghostTransformOrigin.y - topMargin - geoBoundaryMeta.yMin;

		// Shape to show shape border in cropping mode.
		const shapeWithBorder = _.cloneDeep(pageObject);
		ghostImage.shapeId = ghostImage.shapeId + "_shapeWithBorder";
		shapeWithBorder.spPr.fill = undefined;
		shapeWithBorder.spPr.ln = {
			W: 1,
			fill: {
				color: BLUE_COLOR,
				type: FillTypes.SOLID_FILL,
			},
		};

		return (
			<div>
				<div
					styles={{
						boxSizing: "border-box",
						cursor: "move",
						height: "100%",
						left: "0",
						opacity: "0.40",
						position: "absolute",
						top: "0",
						width: "100%",
						zIndex: `${this.properties.poIndex}`,
					}}
				>
					<ShapeContainer
						canvasMode={CanvasMode.EDIT_PICTURE}
						depth={0}
						isSelected={false}
						key="border"
						pageObject={shapeWithBorder}
						partOfGroup={false}
					/>
					<ShapeContainer
						canvasMode={CanvasMode.EDIT_PICTURE}
						depth={0}
						editPageObject={this.properties.editPageObject}
						isSelected={false}
						key="ghost"
						pageObject={ghostImage}
						partOfGroup={this.properties.groupObject !== undefined}
						transformOrigin={ghostTransformOrigin}
						translateObjects={this.properties.translateObjects}
					/>
				</div>
				<div
					styles={{
						boxSizing: "border-box",
						height: "100%",
						left: "0",
						position: "absolute",
						top: "0",
						width: "100%",
						zIndex: `${MAX_Z_INDEX}`,
					}}
				>
					<SelectionContainer
						canvasContainerEl={api.canvasContainerEl}
						isMultiSelect={false}
						noRotationHandle
						pageObject={ghostImage}
						scaleObjects={(scalePointType, delta, currentPosition, bCenter, isMaintainAspectRatio, _, __, transient) => {
							if (this.properties.scaleObjects) {
								const groupId = this.properties.groupObject ? this.properties.groupObject.shapeId : undefined;
								this.properties.scaleObjects(scalePointType, delta, currentPosition, bCenter,
									isMaintainAspectRatio, pageObject.shapeId,
									{ cx: geoBoundaryMeta.width, cy: geoBoundaryMeta.height }, transient, true, groupId );
							}
						}}
						setAllAspectRatios={(transient: boolean) => {
							if (this.properties.setAllAspectRatios && !transient) {
								this.properties.setAllAspectRatios(transient);
							}
						}}
						transformOrigin={ghostTransformOrigin}
					/>
					<EditCurvePoints
						browserType={browserType}
						groupObject={groupObject}
						isCropShape
						pageObject={originalPageObject}
						parentSize={dimensions.position}
						scalePoints
						selectedCurvePoint={selectedCurvePoint}
						setCurvePoints={setCurvePoints}
						setSelectedCurvePoint={setSelectedCurvePoint}
					/>
				</div>
			</div>
		);
	}
}

export default PictureCustomEditCropBox;
